// Ruta: apps/cms/src/App.tsx
import React, { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { FullPageSpinner } from '@heyshaker/ui'
import { ProtectedRoute } from '@heyshaker/auth'
import { ROLES } from '@heyshaker/shared'
import { LoginPage } from './pages/auth/LoginPage.js'

const AdminLayout = lazy(() =>
  import('./layouts/AdminLayout.js').then((m) => ({ default: m.AdminLayout }))
)

// ─── Lazy page imports ────────────────────────────────────────────────────────

const UsersPage = lazy(() =>
  import('./pages/users/UsersPage.js').then((m) => ({ default: m.UsersPage }))
)
const UserDetailPage = lazy(() =>
  import('./pages/users/UserDetailPage.js').then((m) => ({ default: m.UserDetailPage }))
)
const RolesPage = lazy(() =>
  import('./pages/roles/RolesPage.js').then((m) => ({ default: m.RolesPage }))
)
const StoresPage = lazy(() =>
  import('./pages/stores/StoresPage.js').then((m) => ({ default: m.StoresPage }))
)
const StoreDetailPage = lazy(() =>
  import('./pages/stores/StoreDetailPage.js').then((m) => ({ default: m.StoreDetailPage }))
)
const MonitoringPage = lazy(() =>
  import('./pages/monitoring/MonitoringPage.js').then((m) => ({ default: m.MonitoringPage }))
)
const AuditPage = lazy(() =>
  import('./pages/audit/AuditPage.js').then((m) => ({ default: m.AuditPage }))
)
const MetricsPage = lazy(() =>
  import('./pages/metrics/MetricsPage.js').then((m) => ({ default: m.MetricsPage }))
)
const HomePage = lazy(() =>
  import('./pages/home/HomePage.js').then((m) => ({ default: m.HomePage }))
)

const DrinksIndexPage = lazy(() =>
  import('./pages/drinks/DrinksIndexPage.js').then((m) => ({ default: m.DrinksIndexPage }))
)

const ReportsIndexPage = lazy(() =>
  import('./pages/reports/ReportsIndexPage.js').then((m) => ({ default: m.ReportsIndexPage }))
)
const ReportsPage = lazy(() =>
  import('./pages/reports/ReportsPage.js').then((m) => ({ default: m.ReportsPage }))
)
const DrinksListPage = lazy(() =>
  import('./pages/drinks/DrinksListPage.js').then((m) => ({ default: m.DrinksListPage }))
)
const CreateDrinkPage = lazy(() =>
  import('./pages/drinks/CreateDrinkPage.js').then((m) => ({ default: m.CreateDrinkPage }))
)
const FeedbackPage = lazy(() =>
  import('./pages/feedback/FeedbackPage.js').then((m) => ({ default: m.FeedbackPage }))
)
const AlertsPage = lazy(() =>
  import('./pages/alerts/AlertsPage.js').then((m) => ({ default: m.AlertsPage }))
)

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<FullPageSpinner />}>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />

          {/* Protected — superadmin + admin */}
          <Route
            path="/"
            element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN, ROLES.ADMIN]}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<HomePage />} />
            <Route path="home" element={<Navigate to="/" replace />} />

            {/* Users — superadmin + admin (admin only sees arrendatarios, enforced by BFF) */}
            <Route path="users" element={<UsersPage />} />
            <Route path="users/:userId" element={<UserDetailPage />} />

            {/* Roles — superadmin only */}
            <Route path="roles" element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN]}>
                <RolesPage />
              </ProtectedRoute>
            } />

            {/* Stores — superadmin + admin (admin sees only their stores, enforced by BFF) */}
            <Route path="stores" element={<StoresPage />} />
            <Route path="stores/:storeId" element={<StoreDetailPage />} />

            {/* Drinks — superadmin + admin (read-only view) */}
            <Route path="drinks" element={<DrinksIndexPage />} />
            <Route path="stores/:storeId/drinks" element={<DrinksListPage />} />
            <Route path="stores/:storeId/drinks/new" element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN]}>
                <CreateDrinkPage />
              </ProtectedRoute>
            } />

            {/* Reports — superadmin + admin */}
            <Route path="reports" element={<ReportsIndexPage />} />
            <Route path="stores/:storeId/reports" element={<ReportsPage />} />

            {/* Feedback & Alerts — superadmin + admin */}
            <Route path="stores/:storeId/feedback" element={<FeedbackPage />} />
            <Route path="stores/:storeId/alerts" element={<AlertsPage />} />

            {/* Monitoring — superadmin only */}
            <Route path="monitoring" element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN]}>
                <MonitoringPage />
              </ProtectedRoute>
            } />

            {/* Audit — superadmin only */}
            <Route path="audit" element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN]}>
                <AuditPage />
              </ProtectedRoute>
            } />

            {/* Metrics — superadmin only */}
            <Route path="metrics" element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN]}>
                <MetricsPage />
              </ProtectedRoute>
            } />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
